import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useLogHistory } from "providers/LogHistoryProvider";
import { useMissionControl } from "providers/navigation/MissionProvider";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";

import FunctionalButton from "components/layout/FunctionalButton";
import MissionListPanel from "components/Missions/MissionListPanel";
import CreateUpdateMissionSubpage from "components/Missions/CreateUpdateMissionSubpage";
import CreateMissionWizardModal from "components/Missions/CreateMissionWizardModal";
import PagePush from "components/layout/motion/PagePush";
import usePresence from "hooks/usePresence";

import { ReactComponent as AddIcon } from "assets/svgs/Plus.svg";
import { ReactComponent as CloseIcon } from "assets/svgs/Minus.svg";
import { ReactComponent as FilterIcon } from "assets/svgs/search/Filter.svg";
import { ReactComponent as SortIcon } from "assets/svgs/search/Sort.svg";
import { ReactComponent as DeleteIcon } from "assets/svgs/Delete.svg";
import { ReactComponent as CheckedIcon } from "assets/svgs/Checked.svg";
import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";
import { ReactComponent as BinIcon } from "assets/svgs/Editor/bin.svg";

import { useDeleteMissionsMutation } from "app/services/missionStorageApiEnhanced";
import { useGetMissionsQuery } from "app/services/missionStorageApiEnhanced";
import {
    useCreateScheduleMutation,
    useDeleteScheduleMutation,
    useExecuteScheduleMutation,
    useGetSchedulesQuery,
    useUpdateScheduleMutation,
} from "app/services/missionSchedulerApi";
import { setCurrentMission, setIsCreating } from "store/missionCreationSlice";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT, parseBackendUtcDate } from "lib/utils/dateTimeDisplay";

const WEEKDAY_KEYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
];

const MissionManagementPanel = ({ visible = false, className = "", onStartPhotoCapture }) => {
    const { t } = useTranslation(["missions", "common"]);
    const { flags } = useDeviceFeatureFlags();
    const { HAS_SCHEDULER } = flags;
    const { mounted, state: presenceState } = usePresence(visible);
    const TERMINAL_MISSION_STATUSES = new Set(["idle", "completed", "failed", "cancelled", "canceled", "succeeded"]);
    const weekdayOptions = useMemo(
        () => WEEKDAY_KEYS.map((key) => ({ key, label: t(`common:weekdays.${key}`) })),
        [t]
    );
    const formatDuration = useCallback((seconds) => {
        const total = Math.max(0, Number(seconds) || 0);
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const secs = total % 60;
        const parts = [];
        if (hours > 0) parts.push(t("common:duration.hours", { count: hours }));
        if (minutes > 0) parts.push(t("common:duration.minutes", { count: minutes }));
        if (secs > 0 || parts.length === 0) parts.push(t("common:duration.seconds", { count: secs }));
        return parts.join(" ");
    }, [t]);

    const toUtcISOString = (localDateTimeValue) => {
        if (!localDateTimeValue) return undefined;
        const parsed = new Date(localDateTimeValue);
        if (Number.isNaN(parsed.getTime())) return undefined;
        return parsed.toISOString();
    };

    const toLocalDateTimeInputValue = (isoValue) => {
        if (!isoValue) return "";
        const parsed = parseBackendUtcDate(isoValue);
        if (Number.isNaN(parsed.getTime())) return "";
        const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
        return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
    };

    const getNowLocalDateTimeInputValue = () => {
        const now = new Date();
        const offsetMs = now.getTimezoneOffset() * 60 * 1000;
        return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
    };

    const formatDisplayDateTime = (isoValue) => {
        if (!isoValue) return "-";
        return formatLocalDateTime(isoValue, LOCAL_DATETIME_MEDIUM_SHORT);
    };

    const dispatch = useDispatch();
    const {
        showMissionsInControl: showMissions,
        setShowMissionsInControl: setShowMissions,
        showViewMissionSubpage,
        setShowViewMissionSubpage,
        setSelectedMissionId,
        lastAutoOpenedRunningMissionIdRef,
    } = useMissionControl();
    const {
        showLogHistoriesInControl: showLogHistory,
        setShowLogHistoriesInControl: setShowLogHistories,
    } = useLogHistory();
    const {
        isExecuting: isMissionExecuting,
        currentMissionId: runningMissionId,
        executorState,
        clearExecutionDisplay,
    } = useMissionExecution();

    const [lastShowMissions, setLastShowMissions] = useState(showMissions);
    const [lastShowLogHistory, setLastShowLogHistory] = useState(showLogHistory);
    const [showCreateMissionSubpage, setShowCreateMissionSubpage] = useState(false);
    const [showCreateMissionWizard, setShowCreateMissionWizard] = useState(false);
    const [createWizardPrefill, setCreateWizardPrefill] = useState(null);
    const clearCreateWizardPrefill = useCallback(() => setCreateWizardPrefill(null), []);
    const [missionSortMode, setMissionSortMode] = useState("createdAt");
    const [missionSearchQuery, setMissionSearchQuery] = useState("");
    const [scrollToMissionId, setScrollToMissionId] = useState(null);
    const currentMissionId = useSelector((state) => state.missionCreation.currentMissionId);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [editingScheduleId, setEditingScheduleId] = useState(null);
    const [scheduleNameInput, setScheduleNameInput] = useState("");
    const [scheduleMissionIdInput, setScheduleMissionIdInput] = useState("");
    const [schedulePatternInput, setSchedulePatternInput] = useState("weekdays");
    const [scheduleSpecificDateInput, setScheduleSpecificDateInput] = useState("");
    const [scheduleStartDateTimeInput, setScheduleStartDateTimeInput] = useState("");
    const [scheduleEndDateTimeInput, setScheduleEndDateTimeInput] = useState("");
    const [scheduleTimePatternInput, setScheduleTimePatternInput] = useState("interval");
    const [scheduleIntervalValueInput, setScheduleIntervalValueInput] = useState("15");
    const [scheduleIntervalUnitInput, setScheduleIntervalUnitInput] = useState("minutes");
    const [scheduleWeekdaysInput, setScheduleWeekdaysInput] = useState([
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
    ]);
    const [scheduleRepeatStartInput, setScheduleRepeatStartInput] = useState("");
    const [scheduleRepeatEndInput, setScheduleRepeatEndInput] = useState("");
    const [scheduleError, setScheduleError] = useState("");
    const [scheduleExecuteResult, setScheduleExecuteResult] = useState("");

    // Edit Mode State
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const [deleteMissions, { isLoading: isDeleting }] = useDeleteMissionsMutation();
    const { data: missionData } = useGetMissionsQuery(undefined);
    const {
        data: schedulesData,
        isLoading: isSchedulesLoading,
        isError: isSchedulesError,
    } = useGetSchedulesQuery(undefined, { skip: !HAS_SCHEDULER || !showLogHistory });
    const [createSchedule, { isLoading: isCreatingSchedule }] = useCreateScheduleMutation();
    const [updateSchedule, { isLoading: isUpdatingSchedule }] = useUpdateScheduleMutation();
    const [deleteSchedule, { isLoading: isDeletingSchedule }] = useDeleteScheduleMutation();
    const [executeSchedule, { isLoading: isExecutingSchedule }] = useExecuteScheduleMutation();
    const scheduleRows = schedulesData?.schedules || [];
    const missionNameById = new Map((missionData?.missions || []).map((mission) => [mission.id, mission.name]));
    const normalizedExecutorStatus = String(executorState?.status || "").trim().toLowerCase();
    const normalizedExecutorMissionId = String(executorState?.mission_id || "").trim();
    const isExecutorMissionActive =
        Boolean(normalizedExecutorMissionId) && !TERMINAL_MISSION_STATUSES.has(normalizedExecutorStatus);
    const normalizedRunningMissionId = String(runningMissionId || "").trim();
    const activeMissionId =
        (isExecutorMissionActive && normalizedExecutorMissionId) ||
        (isMissionExecuting && normalizedRunningMissionId) ||
        null;
    const hasActiveMission = Boolean(activeMissionId);
    const isMissionListVisible =
        showMissions && !showCreateMissionSubpage;

    useEffect(() => {
        if (HAS_SCHEDULER || !showLogHistory) return;
        setShowLogHistories(false);
        setShowMissions(true);
        setShowScheduleModal(false);
    }, [HAS_SCHEDULER, showLogHistory, setShowLogHistories, setShowMissions]);

    useEffect(() => {
        const activeCount = [showMissions, showLogHistory].filter(Boolean).length;
        if (activeCount > 1) {
            if (lastShowMissions !== showMissions) {
                setShowLogHistories(false);
            } else if (lastShowLogHistory !== showLogHistory) {
                setShowMissions(false);
            }
        }

        setLastShowMissions(showMissions);
        setLastShowLogHistory(showLogHistory);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMissions, showLogHistory]);

    useEffect(() => {
        if (!isEditMode) setSelectedIds([]);
    }, [isEditMode]);

    // Auto-open the running mission subpage when a new run starts.
    // Uses a provider-scoped ref so ControlPage remounts (settings/logs) do not
    // re-trigger open after the user dismissed the subpage, while still
    // preserving an already-open subpage via provider state.
    useEffect(() => {
        if (!activeMissionId) {
            lastAutoOpenedRunningMissionIdRef.current = null;
            return;
        }
        if (lastAutoOpenedRunningMissionIdRef.current === activeMissionId) {
            return;
        }
        lastAutoOpenedRunningMissionIdRef.current = activeMissionId;
        setShowMissions(true);
        setShowLogHistories(false);
        dispatch(setCurrentMission(activeMissionId));
        dispatch(setIsCreating(false));
        clearCreateWizardPrefill();
        setShowCreateMissionSubpage(true);
    }, [
        activeMissionId,
        lastAutoOpenedRunningMissionIdRef,
        setShowMissions,
        setShowLogHistories,
        dispatch,
        clearCreateWizardPrefill,
    ]);

    useEffect(() => {
        if (!showMissions || !currentMissionId || !showCreateMissionSubpage) return;
        setScrollToMissionId(currentMissionId);
    }, [showMissions, currentMissionId, showCreateMissionSubpage]);

    useEffect(() => {
        if (!showMissions || !activeMissionId) return;
        if (showCreateMissionSubpage) return;
        setScrollToMissionId(activeMissionId);
    }, [
        showMissions,
        activeMissionId,
        showCreateMissionSubpage,
    ]);

    const handleToggleSelection = (id) => {
        setSelectedIds(prev => {
            if (prev.includes(id)) return prev.filter(item => item !== id);
            return [...prev, id];
        });
    };

    const handleDelete = async () => {
        if (selectedIds.length > 0) {
            try {
                await deleteMissions({ ids: selectedIds }).unwrap();
                setSelectedIds([]);
                setIsEditMode(false);
            } catch (error) {
                console.error("Failed to delete missions:", error);
            }
        }
    };

    const handleOpenMission = (missionId) => {
        dispatch(setCurrentMission(missionId));
        dispatch(setIsCreating(false));
        clearCreateWizardPrefill();
        setShowCreateMissionSubpage(true);
    };

    const handleCreateMissionWizardComplete = ({ missionId, prefill }) => {
        dispatch(setCurrentMission(missionId));
        dispatch(setIsCreating(true));
        setCreateWizardPrefill(prefill);
        setShowCreateMissionWizard(false);
        setShowCreateMissionSubpage(true);
        setScrollToMissionId(missionId);
    };

    const resetScheduleForm = () => {
        setEditingScheduleId(null);
        setScheduleNameInput("");
        setScheduleMissionIdInput("");
        setSchedulePatternInput("weekdays");
        setScheduleSpecificDateInput("");
        setScheduleStartDateTimeInput("");
        setScheduleEndDateTimeInput("");
        setScheduleTimePatternInput("interval");
        setScheduleIntervalValueInput("15");
        setScheduleIntervalUnitInput("minutes");
        setScheduleWeekdaysInput(["monday", "tuesday", "wednesday", "thursday", "friday"]);
        setScheduleError("");
    };

    const openCreateScheduleModal = () => {
        resetScheduleForm();
        setShowScheduleModal(true);
    };

    const handleSaveSchedule = async () => {
        setScheduleError("");
        setScheduleExecuteResult("");
        if (!scheduleNameInput.trim() || !scheduleMissionIdInput.trim()) {
            setScheduleError(t("schedule.nameAndMissionRequired"));
            return;
        }
        if (schedulePatternInput === "specific_date" && !scheduleSpecificDateInput) {
            setScheduleError(t("schedule.specificDateRequired"));
            return;
        }
        if (
            schedulePatternInput === "specific_date" &&
            new Date(scheduleSpecificDateInput).getTime() <= Date.now()
        ) {
            setScheduleError(t("schedule.specificDateFuture"));
            return;
        }
        if (schedulePatternInput === "weekdays" && scheduleWeekdaysInput.length === 0) {
            setScheduleError(t("schedule.weekdayRequired"));
            return;
        }
        if (
            schedulePatternInput === "weekdays" &&
            (!scheduleStartDateTimeInput || new Date(scheduleStartDateTimeInput).getTime() <= Date.now())
        ) {
            setScheduleError(t("schedule.startDateFuture"));
            return;
        }
        if (
            schedulePatternInput === "weekdays" &&
            scheduleTimePatternInput === "interval" &&
            (!scheduleIntervalValueInput || Number(scheduleIntervalValueInput) <= 0)
        ) {
            setScheduleError(t("schedule.intervalPositive"));
            return;
        }
        if (
            schedulePatternInput === "weekdays" &&
            scheduleStartDateTimeInput &&
            scheduleEndDateTimeInput &&
            new Date(scheduleEndDateTimeInput).getTime() <= new Date(scheduleStartDateTimeInput).getTime()
        ) {
            setScheduleError(t("schedule.endAfterStart"));
            return;
        }
        if (
            schedulePatternInput === "specific_date" &&
            scheduleSpecificDateInput &&
            scheduleEndDateTimeInput &&
            new Date(scheduleEndDateTimeInput).getTime() <= new Date(scheduleSpecificDateInput).getTime()
        ) {
            setScheduleError(t("schedule.endAfterStart"));
            return;
        }

        try {
            const isSpecificDate = schedulePatternInput === "specific_date";
            const isWeekdaysOnce =
                schedulePatternInput === "weekdays" && scheduleTimePatternInput === "once";
            const intervalSeconds =
                schedulePatternInput === "weekdays" && scheduleTimePatternInput === "interval"
                    ? Number(scheduleIntervalValueInput) *
                      (scheduleIntervalUnitInput === "hours" ? 3600 : 60)
                    : undefined;
            const runAtValue = isSpecificDate
                ? toUtcISOString(scheduleSpecificDateInput)
                : toUtcISOString(scheduleStartDateTimeInput);
            const payload = {
                name: scheduleNameInput.trim(),
                mission_id: scheduleMissionIdInput.trim(),
                schedule_type: isSpecificDate || isWeekdaysOnce ? "once" : "repeating",
                run_at: runAtValue,
                interval_seconds: intervalSeconds,
                metadata: {
                    schedule_pattern: schedulePatternInput,
                    time_pattern: isSpecificDate ? "once" : scheduleTimePatternInput,
                    repeat_weekdays: scheduleWeekdaysInput,
                    repeat_start_at: toUtcISOString(scheduleStartDateTimeInput) || null,
                    repeat_end_at: toUtcISOString(scheduleEndDateTimeInput) || null,
                    specific_date_at: toUtcISOString(scheduleSpecificDateInput) || null,
                    interval_value:
                        scheduleTimePatternInput === "interval"
                            ? Number(scheduleIntervalValueInput)
                            : null,
                    interval_unit:
                        scheduleTimePatternInput === "interval" ? scheduleIntervalUnitInput : null,
                },
                enabled: true,
            };
            if (editingScheduleId) {
                await updateSchedule({
                    id: editingScheduleId,
                    ...payload,
                }).unwrap();
            } else {
                await createSchedule(payload).unwrap();
            }
            resetScheduleForm();
            setShowScheduleModal(false);
        } catch (error) {
            setScheduleError(t("schedule.saveFailed"));
        }
    };

    const handleEditSchedule = (schedule) => {
        const schedulePattern =
            schedule.metadata?.schedule_pattern || (schedule.schedule_type === "once" ? "specific_date" : "weekdays");
        setEditingScheduleId(schedule.id);
        setScheduleNameInput(schedule.name || "");
        setScheduleMissionIdInput(schedule.mission_id || "");
        setSchedulePatternInput(schedulePattern);
        setScheduleSpecificDateInput(
            toLocalDateTimeInputValue(schedule.metadata?.specific_date_at || schedule.run_at || schedule.next_run_at)
        );
        setScheduleStartDateTimeInput(toLocalDateTimeInputValue(schedule.metadata?.repeat_start_at || schedule.run_at));
        setScheduleEndDateTimeInput(toLocalDateTimeInputValue(schedule.metadata?.repeat_end_at));
        const rawTimePattern = schedule.metadata?.time_pattern || "interval";
        let normalizedTimePattern =
            rawTimePattern === "specific_time" ? "once" : rawTimePattern;
        if (schedulePattern === "specific_date" || schedule.schedule_type === "once") {
            normalizedTimePattern = "once";
        } else if (normalizedTimePattern !== "once") {
            normalizedTimePattern = "interval";
        }
        setScheduleTimePatternInput(normalizedTimePattern);
        setScheduleIntervalValueInput(
            schedule.metadata?.interval_value
                ? String(schedule.metadata.interval_value)
                : String(Math.max(1, Math.round((schedule.interval_seconds || 900) / 60)))
        );
        setScheduleIntervalUnitInput(schedule.metadata?.interval_unit || "minutes");
        setScheduleWeekdaysInput(
            Array.isArray(schedule.metadata?.repeat_weekdays) && schedule.metadata.repeat_weekdays.length > 0
                ? schedule.metadata.repeat_weekdays
                : ["monday", "tuesday", "wednesday", "thursday", "friday"]
        );
        setScheduleError("");
        setShowScheduleModal(true);
    };

    const toggleWeekday = (weekdayKey) => {
        setScheduleWeekdaysInput((prev) =>
            prev.includes(weekdayKey)
                ? prev.filter((item) => item !== weekdayKey)
                : [...prev, weekdayKey]
        );
    };

    const handleToggleScheduleEnabled = async (schedule) => {
        try {
            const nextEnabled = !schedule.enabled;
            const result = await updateSchedule({ id: schedule.id, enabled: nextEnabled }).unwrap();
            if (nextEnabled) {
                const nextExecution =
                    result?.schedule?.next_run_at || schedule.next_run_at || schedule.run_at || "";
                if (nextExecution) {
                    setScheduleExecuteResult(
                        t("schedule.enabledNext", {
                            name: schedule.name,
                            datetime: formatDisplayDateTime(nextExecution),
                        })
                    );
                } else {
                    setScheduleExecuteResult(t("schedule.enabled", { name: schedule.name }));
                }
            } else {
                setScheduleExecuteResult(t("schedule.disabled", { name: schedule.name }));
            }
        } catch (error) {
            setScheduleError(t("schedule.toggleFailed"));
        }
    };

    const renderTopHalf = (schedule) => {
        const pattern = schedule.metadata?.schedule_pattern || (schedule.schedule_type === "once" ? "specific_date" : "weekdays");
        if (pattern === "specific_date") {
            return t("schedule.specificDate", {
                datetime: formatDisplayDateTime(
                    schedule.metadata?.specific_date_at || schedule.run_at || schedule.next_run_at
                ),
            });
        }
        const weekdays = Array.isArray(schedule.metadata?.repeat_weekdays)
            ? schedule.metadata.repeat_weekdays
            : [];
        const compactLabel = weekdays
            .map((day) => t(`common:weekdays.${day}`))
            .join(" ");
        return t("schedule.weekdays", { days: compactLabel || t("common:notSet") });
    };

    const renderBottomHalf = (schedule) => {
        const timePattern = schedule.metadata?.time_pattern || "interval";
        if (timePattern === "once" || schedule.schedule_type === "once") {
            return t("common:once");
        }
        const intervalValue = schedule.metadata?.interval_value;
        const intervalUnitRaw = schedule.metadata?.interval_unit || "minutes";
        const intervalUnit = intervalUnitRaw === "hours"
            ? t("common:intervalUnit.hours")
            : t("common:intervalUnit.minutes");
        if (intervalValue) {
            return t("schedule.everyInterval", { value: intervalValue, unit: intervalUnit });
        }
        return t("schedule.everyMinutesFallback", {
            count: Math.max(1, Math.round((schedule.interval_seconds || 0) / 60)),
        });
    };

    const handleDeleteSchedule = async (scheduleId) => {
        setScheduleError("");
        setScheduleExecuteResult("");
        try {
            await deleteSchedule(scheduleId).unwrap();
            if (editingScheduleId === scheduleId) {
                resetScheduleForm();
            }
        } catch (error) {
            setScheduleError(t("schedule.removeFailed"));
        }
    };

    const handleExecuteSchedule = async (scheduleId) => {
        setScheduleError("");
        setScheduleExecuteResult("");
        setScheduleExecuteResult(t("schedule.startingExecution"));
        try {
            const result = await executeSchedule(scheduleId).unwrap();
            if (result.queued) {
                const countdownText =
                    result.seconds_until_run !== undefined
                        ? t("schedule.away", { duration: formatDuration(result.seconds_until_run) })
                        : "";
                setScheduleExecuteResult(
                    result.message || t("schedule.armed", { runAt: result.run_at, countdown: countdownText })
                );
                return;
            }
            setScheduleExecuteResult(
                result.success
                    ? t("schedule.executed", { missionId: result.mission_id })
                    : t("schedule.executionFailed", { code: result.return_code })
            );
        } catch (error) {
            const backendMessage =
                toUserFacingApiError(error?.data?.error || error?.data?.stderr || error?.error, t("schedule.executeFailed"));
            setScheduleError(t("schedule.executeFailedDetail", { detail: backendMessage }));
        }
    };

    const isScheduleMissionRunning = (schedule) => {
        const scheduleMissionId = String(schedule.mission_id || "").trim();
        if (!scheduleMissionId) return false;
        if (activeMissionId && scheduleMissionId === activeMissionId) return true;
        if (
            isExecutorMissionActive &&
            scheduleMissionId === normalizedExecutorMissionId
        ) {
            return true;
        }
        if (
            isMissionExecuting &&
            scheduleMissionId === normalizedRunningMissionId
        ) {
            return true;
        }
        if (
            (normalizedExecutorStatus === "running" ||
                normalizedExecutorStatus === "paused") &&
            scheduleMissionId === normalizedExecutorMissionId
        ) {
            return true;
        }
        return false;
    };

    const isStaleSchedule = (schedule) => {
        if (schedule.schedule_type !== "once") return false;
        if (isScheduleMissionRunning(schedule)) return false;
        if (schedule.last_executed_at) return true;
        if (!schedule.next_run_at) return false;
        const dueAt = parseBackendUtcDate(schedule.next_run_at);
        if (Number.isNaN(dueAt.getTime())) return true;
        return dueAt.getTime() <= Date.now();
    };

    return (
        mounted && (
            <div
                className={`ios-float-panel ${className} bottom-[15rem] w-[30.75rem] min-h-0 overflow-hidden`}
                data-state={presenceState}
            >
                {/* bottom-[15rem]: clear joystick row + speed slider (see ControlPage) */}
                <div className="flex h-full min-h-0 w-[30.75rem] flex-col rounded-r10 bg-black90 px-2 py-4">
                    <div className="flex w-full items-center justify-between">
                        <div className="flex items-center justify-start space-x-4">
                            <p
                                className={`h4l cursor-pointer font-bold transition-all ${!showMissions && "text-black60"
                                    }`}
                                onClick={() => {
                                    setShowMissions(true);
                                    setShowLogHistories(false);
                                }}
                            >
                                {t("panel.mission")}
                            </p>
                            {HAS_SCHEDULER && (
                                <p
                                    className={`h4l cursor-pointer font-bold transition-all ${!showLogHistory && "text-black60"
                                        }`}
                                    onClick={() => {
                                        setShowMissions(false);
                                        setShowLogHistories(true);
                                    }}
                                >
                                    {t("panel.schedule")}
                                </p>
                            )}
                        </div>
                        <FunctionalButton
                            icon={<CloseIcon className="w-4" />}
                            className="h-6 w-6 rounded-r3 hover:bg-black70"
                            onClick={() => {
                                clearExecutionDisplay();
                                setShowViewMissionSubpage(false);
                                setSelectedMissionId(null);
                                setShowMissions(false);
                                setShowLogHistories(false);
                            }}
                        />
                    </div>
                    <div className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden">
                        {showMissions && (
                            <MissionListPanel
                                className="min-h-0 flex-1 bg-transparent p-0"
                                missions={(missionData?.missions || []).map((mission) => ({
                                    key: mission.id,
                                    sourceMissionId: mission.id,
                                    name: mission.name,
                                    created_at: mission.createdAt,
                                    action_count: mission.actions.length,
                                }))}
                                onSelect={(mission) => {
                                    handleOpenMission(mission.sourceMissionId);
                                }}
                                searchQuery={missionSearchQuery}
                                onSearchQueryChange={setMissionSearchQuery}
                                sortMode={missionSortMode}
                                onSortModeChange={setMissionSortMode}
                                activeMissionId={activeMissionId}
                                isMissionActive={hasActiveMission}
                                isEditMode={isEditMode}
                                selectedIds={selectedIds}
                                onToggleSelection={handleToggleSelection}
                                onEnterEditMode={() => setIsEditMode(true)}
                                isDeleting={isDeleting}
                                scrollToMissionId={scrollToMissionId}
                                scrollWhenVisible={isMissionListVisible}
                                toolbarContent={
                                    <div className="flex shrink-0 space-x-2">
                                        {!isEditMode && (
                                            <FunctionalButton
                                                icon={<AddIcon className="w-4" />}
                                                className="icon-button h-6 w-6 rounded-r3 hover:bg-black70"
                                                onClick={() => {
                                                    dispatch(setCurrentMission(null));
                                                    dispatch(setIsCreating(false));
                                                    clearCreateWizardPrefill();
                                                    setShowCreateMissionWizard(true);
                                                }}
                                            />
                                        )}
                                        <FunctionalButton
                                            icon={<FilterIcon className="w-4" />}
                                            className="h-6 w-6 rounded-r3 hover:bg-black70"
                                        />
                                        <FunctionalButton
                                            icon={<SortIcon className="w-4" />}
                                            className="h-6 w-6 rounded-r3 hover:bg-black70"
                                            onClick={() => setMissionSortMode((prev) =>
                                                prev === "createdAt" ? "name" : "createdAt"
                                            )}
                                        />
                                        {isEditMode && (
                                            <>
                                                <FunctionalButton
                                                    icon={<DeleteIcon className="w-4" />}
                                                    className={`h-6 w-6 rounded-r3 hover:bg-black70 ${selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                                                    onClick={handleDelete}
                                                />
                                                <FunctionalButton
                                                    icon={<CheckedIcon className="w-4" />}
                                                    className="h-6 w-6 rounded-r3 hover:bg-black70"
                                                    onClick={() => setIsEditMode(false)}
                                                />
                                            </>
                                        )}
                                    </div>
                                }
                            />
                        )}
                        {showLogHistory && HAS_SCHEDULER && (
                            <div className="scroll-vertical min-h-0 w-full flex-1 space-y-3 overflow-y-auto px-2">
                                <div className="flex justify-end">
                                    <FunctionalButton
                                        title={t("common:create")}
                                        className="h-8 rounded-r5 border border-primary2 bg-primary2 px-4 text-h5"
                                        onClick={openCreateScheduleModal}
                                    />
                                </div>

                                {scheduleError && <p className="text-h5 text-warning">{scheduleError}</p>}
                                {scheduleExecuteResult && (
                                    <p className="text-h5 text-primary2">{scheduleExecuteResult}</p>
                                )}
                                {(isCreatingSchedule ||
                                    isUpdatingSchedule ||
                                    isDeletingSchedule ||
                                    isExecutingSchedule) && (
                                        <p className="text-h5 text-black40">{t("schedule.processing")}</p>
                                    )}
                                {isSchedulesLoading && (
                                    <p className="text-h5 text-black40">{t("schedule.loading")}</p>
                                )}
                                {isSchedulesError && (
                                    <p className="text-h5 text-warning">
                                        {t("schedule.loadFailed")}
                                    </p>
                                )}
                                {scheduleRows.map((schedule) => {
                                    const isScheduleExecuting = isScheduleMissionRunning(schedule);
                                    const staleSchedule = !isScheduleExecuting && isStaleSchedule(schedule);
                                    return (
                                        <div
                                            key={schedule.id}
                                            className="flex w-full flex-col space-y-2 rounded-r5 border-2 border-black70 p-2"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-h5 font-semibold">{t("schedule.scheduleLabel", { name: schedule.name })}</p>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-h5 text-black40">
                                                        {t("schedule.missionLabel", {
                                                            name: missionNameById.get(schedule.mission_id) || schedule.mission_id,
                                                        })}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        className="flex h-6 w-6 items-center justify-center rounded-r5 border border-black70 bg-black90 hover:bg-black80"
                                                        onClick={() => handleEditSchedule(schedule)}
                                                        aria-label={t("schedule.editAriaLabel", { name: schedule.name })}
                                                    >
                                                        <PencilIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="flex h-6 w-6 items-center justify-center rounded-r5 border border-black70 bg-black90 hover:bg-black80"
                                                        onClick={() => handleDeleteSchedule(schedule.id)}
                                                        aria-label={t("schedule.removeAriaLabel", { name: schedule.name })}
                                                    >
                                                        <BinIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="rounded-r5 border border-black70 bg-black90 p-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-h5">{renderTopHalf(schedule)}</p>
                                                    <button
                                                        type="button"
                                                        className={`h-6 w-12 shrink-0 rounded-full border transition-all ${schedule.enabled
                                                                ? "border-primary2 bg-primary2"
                                                                : "border-black70 bg-black80"
                                                            }`}
                                                        onClick={() => handleToggleScheduleEnabled(schedule)}
                                                    >
                                                        <span
                                                            className={`block h-4 w-4 rounded-full bg-white transition-all ${schedule.enabled ? "translate-x-6" : "translate-x-1"
                                                                }`}
                                                        />
                                                    </button>
                                                </div>
                                                <p className="mt-2 text-h5 text-black40">{renderBottomHalf(schedule)}</p>
                                            </div>
                                            {schedule.next_run_at && (
                                                <p className="text-h5 text-black40">
                                                    {t("schedule.nextRun", { datetime: formatDisplayDateTime(schedule.next_run_at) })}
                                                </p>
                                            )}
                                            {isScheduleExecuting && (
                                                <p className="text-h5 text-primary2">
                                                    {t("schedule.missionInProgress")}
                                                </p>
                                            )}
                                            {staleSchedule && (
                                                <p className="text-h5 text-warning">
                                                    {t("schedule.stale")}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {HAS_SCHEDULER && showLogHistory && showScheduleModal && (
                    <div className="absolute inset-0 z-[120] flex items-center justify-center bg-black/70">
                        <div className="max-h-[90%] w-[28rem] overflow-y-auto rounded-r10 border border-black70 bg-black100 p-4">
                            <div className="mb-3 flex items-center justify-between">
                                <p className="text-h4 font-semibold">
                                    {editingScheduleId ? t("schedule.editTitle") : t("schedule.createTitle")}
                                </p>
                                <FunctionalButton
                                    title={t("common:close")}
                                    className="h-6 w-6 rounded-r5 border border-black70 bg-black90 text-h5"
                                    onClick={() => {
                                        setShowScheduleModal(false);
                                        resetScheduleForm();
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <div className="flex w-full flex-col gap-1">
                                    <p className="text-h5 text-black40">{t("schedule.nameLabel")}</p>
                                    <input
                                        value={scheduleNameInput}
                                        onChange={(event) => setScheduleNameInput(event.target.value)}
                                        placeholder={t("schedule.namePlaceholder")}
                                        className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                    />
                                </div>
                                <div className="flex w-full flex-col gap-1">
                                    <p className="text-h5 text-black40">{t("schedule.selectMission")}</p>
                                    <select
                                        value={scheduleMissionIdInput}
                                        onChange={(event) => setScheduleMissionIdInput(event.target.value)}
                                        className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                    >
                                        <option value="">{t("schedule.selectMission")}</option>
                                        {(missionData?.missions || []).map((mission) => (
                                            <option key={mission.id} value={mission.id}>
                                                {mission.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex w-full flex-col gap-1">
                                    <p className="text-h5 text-black40">{t("schedule.executionPattern")}</p>
                                    <select
                                        value={schedulePatternInput}
                                        onChange={(event) => {
                                            const nextPattern = event.target.value;
                                            setSchedulePatternInput(nextPattern);
                                            if (nextPattern === "specific_date") {
                                                setScheduleTimePatternInput("once");
                                            } else if (scheduleTimePatternInput === "once") {
                                                setScheduleTimePatternInput("interval");
                                            }
                                        }}
                                        className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                    >
                                        <option value="weekdays">{t("schedule.intervalWeekdays")}</option>
                                        <option value="specific_date">{t("schedule.specificDateOption")}</option>
                                    </select>
                                </div>
                                {schedulePatternInput === "weekdays" && (
                                    <>
                                        <div className="flex w-full flex-col gap-1">
                                            <p className="text-h5 text-black40">{t("schedule.weekdaysLabel")}</p>
                                            <div className="grid w-full grid-cols-7 gap-2">
                                                {weekdayOptions.map((weekday) => {
                                                    const active = scheduleWeekdaysInput.includes(weekday.key);
                                                    return (
                                                        <button
                                                            key={weekday.key}
                                                            type="button"
                                                            className={`w-full rounded-r5 border px-0 py-1 text-center text-h5 ${active
                                                                    ? "border-primary2 bg-primary2 text-white"
                                                                    : "border-black70 bg-black90 text-black40"
                                                                }`}
                                                            onClick={() => toggleWeekday(weekday.key)}
                                                        >
                                                            {weekday.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="flex w-full flex-col gap-1">
                                            <p className="text-h5 text-black40">{t("schedule.startDateTime")}</p>
                                            <input
                                                type="datetime-local"
                                                value={scheduleStartDateTimeInput}
                                                min={getNowLocalDateTimeInputValue()}
                                                onChange={(event) =>
                                                    setScheduleStartDateTimeInput(event.target.value)
                                                }
                                                className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                            />
                                        </div>
                                        <div className="flex w-full flex-col gap-1">
                                            <p className="text-h5 text-black40">{t("schedule.endDateTimeOptional")}</p>
                                            <input
                                                type="datetime-local"
                                                value={scheduleEndDateTimeInput}
                                                min={scheduleStartDateTimeInput || getNowLocalDateTimeInputValue()}
                                                disabled={!scheduleStartDateTimeInput}
                                                onChange={(event) =>
                                                    setScheduleEndDateTimeInput(event.target.value)
                                                }
                                                className={`w-full rounded-r5 border px-2 py-1 text-h5 ${scheduleStartDateTimeInput
                                                        ? "border-black70 bg-black90"
                                                        : "cursor-not-allowed border-black80 bg-black80 text-black50"
                                                    }`}
                                            />
                                        </div>
                                    </>
                                )}
                                {schedulePatternInput === "specific_date" && (
                                    <>
                                        <div className="flex w-full flex-col gap-1">
                                            <p className="text-h5 text-black40">{t("schedule.specificDateTime")}</p>
                                            <input
                                                type="datetime-local"
                                                value={scheduleSpecificDateInput}
                                                min={getNowLocalDateTimeInputValue()}
                                                onChange={(event) => setScheduleSpecificDateInput(event.target.value)}
                                                className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                            />
                                        </div>
                                    </>
                                )}
                                {schedulePatternInput === "weekdays" && (
                                    <>
                                        <div className="flex w-full flex-col gap-1">
                                            <p className="text-h5 text-black40">{t("schedule.timePattern")}</p>
                                            <select
                                                value={scheduleTimePatternInput}
                                                onChange={(event) =>
                                                    setScheduleTimePatternInput(event.target.value)
                                                }
                                                className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                            >
                                                <option value="interval">{t("schedule.interval")}</option>
                                                <option value="once">{t("common:once")}</option>
                                            </select>
                                        </div>
                                        {scheduleTimePatternInput === "interval" && (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={scheduleIntervalValueInput}
                                                    onChange={(event) =>
                                                        setScheduleIntervalValueInput(event.target.value)
                                                    }
                                                    className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                                />
                                                <select
                                                    value={scheduleIntervalUnitInput}
                                                    onChange={(event) =>
                                                        setScheduleIntervalUnitInput(event.target.value)
                                                    }
                                                    className="w-full rounded-r5 border border-black70 bg-black90 px-2 py-1 text-h5"
                                                >
                                                    <option value="minutes">{t("schedule.minutes")}</option>
                                                    <option value="hours">{t("schedule.hours")}</option>
                                                </select>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {scheduleError && <p className="mt-3 text-h5 text-warning">{scheduleError}</p>}
                            <div className="mt-4 flex gap-2">
                                <FunctionalButton
                                    title={editingScheduleId ? t("common:update") : t("common:create")}
                                    className="h-8 rounded-r5 border border-primary2 bg-primary2 px-4 text-h5"
                                    onClick={handleSaveSchedule}
                                />
                                <FunctionalButton
                                    title={t("common:cancel")}
                                    className="h-8 rounded-r5 border border-black70 bg-black90 px-4 text-h5"
                                    onClick={() => {
                                        setShowScheduleModal(false);
                                        resetScheduleForm();
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {showMissions && showCreateMissionWizard && (
                    <CreateMissionWizardModal
                        visible
                        onClose={() => setShowCreateMissionWizard(false)}
                        onComplete={handleCreateMissionWizardComplete}
                    />
                )}

                {/* Overlaid Subpage — create or open mission (run + edit on one page) */}
                <PagePush visible={showCreateMissionSubpage}>
                    <CreateUpdateMissionSubpage
                        wizardPrefill={createWizardPrefill}
                        onWizardPrefillConsumed={clearCreateWizardPrefill}
                        onBack={() => {
                            setShowCreateMissionSubpage(false);
                            window.setTimeout(() => clearCreateWizardPrefill(), 220);
                        }}
                    />
                </PagePush>
            </div>
        )
    );
};

export default MissionManagementPanel;
